import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import { api } from "@/lib/api";
import { ExternalLink, Printer, ShieldCheck, AlertTriangle } from "lucide-react";

const WHEN_LABEL = {
  next_30_days: "Next 30 days",
  this_semester: "This semester",
  next_semester: "Next semester",
  before_transfer: "Before transfer",
};

export default function MyPlan() {
  const { plan_id } = useParams();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(false);
  useEffect(() => {
    api.get(`/plans/${plan_id}`).then((r) => setPlan(r.data)).catch(() => setErr(true)).finally(() => setLoading(false));
  }, [plan_id]);

  if (loading) return <Layout><div className="max-w-3xl mx-auto p-16 text-stone">Loading your plan…</div></Layout>;
  if (err || !plan) return <Layout><div className="max-w-3xl mx-auto p-16"><h1 className="font-serif text-3xl text-ivy">Plan not found</h1><Link to="/build-my-plan" className="mt-4 inline-block text-brass border-b border-brass">Build a new one →</Link></div></Layout>;

  const p = plan.plan_json || {};
  const created = new Date(plan.created_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
  const first = plan.intake?.first_name || "there";

  return (
    <Layout>
      <SEO noindex title="Your Transfer Plan | Beyond Valencia" description="A personalized Florida transfer plan grounded in verified admissions data." canonical={`/my-plan/${plan_id}`} />
      <style>{`@media print{header,footer,button[data-print-hide]{display:none!important}body{background:#F5F1E8!important}main{max-width:none!important;padding:20px!important}}`}</style>

      <section className="bg-ivy text-bone border-b border-ivy-light" data-testid="my-plan-hero">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 py-14 lg:py-20">
          <div className="text-[10px] uppercase tracking-[0.24em] text-brass-soft font-medium">Beyond Valencia Transfer Plan</div>
          <h1 className="mt-4 font-serif font-medium text-4xl sm:text-5xl tracking-tight leading-[1.05]">Your transfer plan, {first}.</h1>
          <p className="mt-4 text-sm text-bone-dim">Generated {created} · Plan ID {plan_id}</p>
          <button onClick={() => window.print()} data-print-hide className="mt-6 inline-flex items-center gap-2 border border-bone-dim/40 hover:border-bone text-bone uppercase tracking-[0.18em] text-[11px] font-semibold py-2 px-4" data-testid="my-plan-print">
            <Printer className="h-3.5 w-3.5" /> Print / save PDF
          </button>
        </div>
      </section>

      <section className="bg-bone">
        <main className="max-w-3xl mx-auto px-5 sm:px-8 py-14 lg:py-16 space-y-14">

          {/* Summary */}
          <div className="border-l-4 border-brass pl-5" data-testid="plan-summary">
            <div className="text-[10px] uppercase tracking-[0.22em] text-brass font-semibold">Summary</div>
            <p className="mt-3 text-lg text-ivy leading-relaxed font-serif">{p.summary}</p>
          </div>

          {/* Recommended focus */}
          {p.recommended_focus?.length > 0 && (
            <div data-testid="plan-focus">
              <h2 className="font-serif text-2xl text-ivy tracking-tight">Recommended focus</h2>
              <ul className="mt-4 space-y-2">
                {p.recommended_focus.map((f, i) => (
                  <li key={i} className="flex gap-3 text-sm text-ink"><span className="text-brass mt-2.5 h-px w-4 bg-brass flex-shrink-0" />{f}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Target school assessments */}
          {p.target_school_assessments?.length > 0 && (
            <div data-testid="plan-targets">
              <h2 className="font-serif text-2xl text-ivy tracking-tight">Target schools</h2>
              <div className="mt-5 space-y-4">
                {p.target_school_assessments.map((a, i) => (
                  <details key={i} className="border border-stone/40 bg-bone-dim/20" data-testid={`plan-target-${a.institution_slug}`}>
                    <summary className="cursor-pointer flex items-center justify-between gap-3 p-4 select-none">
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.22em] text-brass font-semibold">{a.fit}</div>
                        <div className="text-ivy font-serif text-lg mt-1">{a.institution_slug}</div>
                      </div>
                      <span className="text-[11px] uppercase tracking-[0.18em] text-stone">{a.gpa_signal}</span>
                    </summary>
                    <div className="px-4 pb-5 space-y-3 border-t border-stone/30">
                      {a.notes && <p className="text-sm text-ink-soft mt-3">{a.notes}</p>}
                      {a.critical_gaps?.length > 0 && (
                        <div>
                          <div className="text-[10px] uppercase tracking-[0.22em] text-brass mt-2">Critical gaps</div>
                          <ul className="mt-2 space-y-1">{a.critical_gaps.map((g, k) => <li key={k} className="text-sm text-ink">• {g}</li>)}</ul>
                        </div>
                      )}
                      {a.sources?.length > 0 && (
                        <div className="pt-2 flex flex-wrap gap-2">
                          {a.sources.map((s, k) => (
                            s.url ? (
                              <a key={k} href={s.url} target="_blank" rel="noopener noreferrer nofollow" className="text-[11px] text-brass border-b border-brass/50 hover:text-brass-soft inline-flex items-center gap-1">
                                {s.verification_record_id && <ShieldCheck className="h-3 w-3" />}
                                <ExternalLink className="h-3 w-3" />{s.label}
                              </a>
                            ) : (
                              <span key={k} className="text-[11px] text-stone border-b border-stone/40">{s.label}</span>
                            )
                          ))}
                        </div>
                      )}
                    </div>
                  </details>
                ))}
              </div>
            </div>
          )}

          {/* Milestones grouped */}
          {p.milestones?.length > 0 && (
            <div data-testid="plan-milestones">
              <h2 className="font-serif text-2xl text-ivy tracking-tight">Your milestones</h2>
              <div className="mt-5 space-y-6">
                {["next_30_days", "this_semester", "next_semester", "before_transfer"].map((w) => {
                  const items = p.milestones.filter((m) => m.when === w);
                  if (!items.length) return null;
                  return (
                    <div key={w}>
                      <div className="text-[10px] uppercase tracking-[0.22em] text-brass font-semibold">{WHEN_LABEL[w]}</div>
                      <ul className="mt-3 space-y-3">
                        {items.map((m, i) => (
                          <li key={i} className="border-l-2 border-stone/40 pl-4">
                            <div className="text-ivy font-medium text-sm">{m.title}</div>
                            <div className="text-sm text-ink-soft mt-1">{m.detail}</div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Risks */}
          {p.risks?.length > 0 && (
            <div className="border-l-4 border-crimson bg-crimson/5 px-5 py-4" data-testid="plan-risks">
              <div className="flex items-baseline gap-2 mb-2"><AlertTriangle className="h-4 w-4 text-crimson" /><div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-crimson">Risks</div></div>
              <ul className="space-y-1.5 text-sm text-ink-soft">{p.risks.map((r, i) => <li key={i}>• {r}</li>)}</ul>
            </div>
          )}

          {/* Next conversations */}
          {p.next_conversation_starters?.length > 0 && (
            <div data-testid="plan-conversations">
              <h2 className="font-serif text-2xl text-ivy tracking-tight">Talk to your adviser about</h2>
              <ul className="mt-4 space-y-2">{p.next_conversation_starters.map((c, i) => <li key={i} className="text-sm text-ink-soft border-l border-brass pl-3">{c}</li>)}</ul>
            </div>
          )}

          {/* Talk to founder */}
          <div className="border border-brass/40 bg-brass/5 p-6 lg:p-8" data-testid="plan-founder-cta">
            <div className="text-[11px] uppercase tracking-[0.24em] text-brass font-semibold">Talk it through</div>
            <h3 className="mt-3 font-serif text-2xl text-ivy tracking-tight">Book a strategy session with Taimur.</h3>
            <p className="mt-3 text-sm text-ink-soft">Every plan reads better with 45 minutes of live discussion. Book a Transfer Audit for a personalized deep dive.</p>
            <div className="mt-5 flex flex-wrap gap-3">
              <Link to="/transfer-audit" className="bg-brass hover:bg-brass-soft text-ink uppercase tracking-[0.18em] text-[11px] font-semibold py-3 px-5" data-testid="plan-book-audit">Book a Transfer Audit</Link>
              <a href="mailto:founder@ivyleagueconsulting.org" className="border border-ivy text-ivy hover:bg-ivy hover:text-bone uppercase tracking-[0.18em] text-[11px] font-semibold py-3 px-5">Email the founder</a>
            </div>
          </div>
        </main>
      </section>
    </Layout>
  );
}
